import { useState } from "react";
import { Send } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { useLeadSubmission } from "@/hooks/useLeadSubmission";

interface ContactFormSectionProps {
  title?: string;
  description?: string;
  hideMessage?: boolean;
}

export function ContactFormSection({ title, description, hideMessage }: ContactFormSectionProps) {
  const { t } = useLanguage();
  const { submitLead, isSubmitting } = useLeadSubmission({ formType: "contact" });
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    message: "",
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      return;
    }

    const success = await submitLead(formData);
    
    if (success) {
      setFormData({ name: "", company: "", phone: "", email: "", message: "" });
    }
  };

  const displayTitle = title || t("footer.form.title");
  const displayDescription = description || t("footer.form.description");

  return (
    <section id="contact-form" className="bg-primary text-primary-foreground scroll-mt-20">
      <div className="container py-12">
        <div className="bg-primary-foreground/10 rounded-2xl p-6 md:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold mb-2">{displayTitle}</h3>
              <p className="text-primary-foreground/70 text-sm">
                {displayDescription}
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-name" className="text-primary-foreground/90">
                    {t("budget.form.name")} *
                  </Label>
                  <Input
                    id="contact-name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t("budget.form.namePlaceholder")}
                    className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-company" className="text-primary-foreground/90">
                    {t("footer.form.company")}
                  </Label>
                  <Input
                    id="contact-company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder={t("footer.form.companyPlaceholder")}
                    className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-phone" className="text-primary-foreground/90">
                    {t("budget.form.phone")} *
                  </Label>
                  <PhoneInput
                    id="contact-phone"
                    value={formData.phone}
                    onChange={(value) => setFormData((prev) => ({ ...prev, phone: value }))}
                    className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email" className="text-primary-foreground/90">
                    {t("budget.form.email")}
                  </Label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="email@company.com"
                    className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50"
                  />
                </div>
              </div>
              
              {hideMessage && (
                <div className="flex justify-center">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    size="lg"
                    className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 px-12"
                  >
                    {isSubmitting ? (
                      t("budget.form.sending")
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {t("footer.form.submit")}
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              {!hideMessage && (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  <div className="lg:col-span-3 space-y-2">
                    <Label htmlFor="contact-message" className="text-primary-foreground/90">
                      {t("footer.form.message")}
                    </Label>
                    <Textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder={t("footer.form.messagePlaceholder")}
                      rows={2}
                      className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 resize-none"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                    >
                      {isSubmitting ? (
                        t("budget.form.sending")
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {t("footer.form.submit")}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
